import React from 'react';
import styled from 'styled-components';
import { Flex, Spinner, Suspense, Text } from 'core/components';
import { Measurement } from '@kentik/ui-shared/query/query.types';
import { AppProtocolsDictionary } from '@kentik/ui-shared/dictionary/types';
import { MeasurementHash, useDictionaryMeasurement } from 'app/stores/dictionary/useDictionaryMeasurements';
import { UdeFormProps } from './form.types';
import UdePanelMetrics from './UdePanelMetrics';
import UdePanelDimensions from './UdePanelDimensions';

interface SelectedPanelProps {
  form: UdeFormProps['form'];
  measurements: Measurement[];
  measurementHash: MeasurementHash;
  appProtocols?: AppProtocolsDictionary;
  onReset: () => void;
  headerVariant?: 'default' | 'pane';
}

const SelectedContainer = styled(Flex)`
  flex-direction: column;
  width: 100%;
  flex-shrink: 0;
  overflow-y: auto;
  scrollbar-width: thin;
`;

/**
 * Selected panel: Shows selected units and dimensions with their existing
 * edit UIs (rollup controls, sortable menu, filter editing).
 * Displayed inline below the browse panel.
 */
const SelectedPanel = ({
  form,
  measurements,
  measurementHash,
  appProtocols,
  onReset: _onReset,
  headerVariant = 'default'
}: SelectedPanelProps) => {
  const measurementName = form.getValue('config.data.measurement');
  const { isLoading: isLoadingMeasurement } = useDictionaryMeasurement(measurementName);

  if (!measurementName) {
    return null;
  }

  return (
    <SelectedContainer>
      <Suspense
        loading={isLoadingMeasurement}
        fallback={
          <Flex gap={1} p={2}>
            <Spinner size={16} />
            <Text muted>Loading...</Text>
          </Flex>
        }
      >
        <>
          <UdePanelMetrics measurements={measurements} appProtocols={appProtocols} headerVariant={headerVariant} />
          <UdePanelDimensions
            measurements={measurements}
            headerVariant={headerVariant}
            measurementHash={measurementHash}
            appProtocols={appProtocols}
          />
        </>
      </Suspense>
    </SelectedContainer>
  );
};

SelectedPanel.displayName = 'SelectedPanel';

export default SelectedPanel;
